<template>
  <div class="overview">
    <router-view />
    <div class="content--main">
      <div class="row">
        <div class="col-6">
          <div class="block block--welcome">
            <div class="block-content p-0">
              <h2 class="mb-1">Hi Admin,</h2>
              <p>Here's what's happening today:</p>
              <p>
                <span>{{ coworkingCount }}</span>
                {{ coworkingCount === 1 ? "Coworking Space" : "Desks" }}
              </p>
              <p>
                <span>{{ conferenceCount }}</span>
                {{
                  conferenceCount === 1 ? "Conference Room" : "Conference Rooms"
                }}
              </p>
              <p>
                <span v-if="events">{{ events.length }}</span>
                <Spinner :theme="primary" v-else />
                Upcoming Events
              </p>
              <p>
                You have a total of
                <span class="total--count" :style="{ color: primary }">{{
                  coworkingCount + conferenceCount
                }}</span>
                bookings today.
              </p>
            </div>
          </div>
        </div>
        <div class="col-6">
          <div class="block block--total-sales">
            <div class="block-header p-0">
              <h4>
                Sales for this Month
              </h4>
            </div>
            <div class="block-content p-0 block--sales-month d-flex">
              <template v-if="isComputed">
                <div
                  v-for="(sale, i) in projectedSales"
                  :key="i"
                  class="d-flex flex-column mt-3"
                >
                  <span>{{ sale.month }}</span>
                  <p>
                    {{
                      `P ${sale.amount.toLocaleString("en-US", {
                        minimumFractionDigits: 2
                      })}`
                    }}
                  </p>
                </div>
              </template>
              <Spinner :theme="primary" v-else />
            </div>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-6">
          <div class="block">
            <h4>
              Sales for conference rooms and coworking desks this year
            </h4>
            <LineChart
              v-if="
                salesCollection.datasets[0].data.length == 12 &&
                  salesCollection.datasets[1].data.length == 12
              "
              :chart-data="salesCollection"
              :options="salesChartOptions"
            />
            <Spinner :theme="primary" v-else />
          </div>
        </div>
        <div class="col-6">
          <div class="block">
            <h4>
              Bookings of conference rooms and coworking desks this year
            </h4>
            <LineChart
              v-if="
                usageCollection.datasets[0].data.length == 12 &&
                  usageCollection.datasets[1].data.length == 12
              "
              :chart-data="usageCollection"
              :options="usageChartOptions"
            />
            <Spinner :theme="primary" v-else />
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col">
          <div class="block">
            <h4>
              Conference Room Usage
            </h4>
            <div class="d-flex barchart--block">
              <div class="barchart--holder" v-if="barchartLabel.length != 0">
                <h6>This Month</h6>
                <div class="d-flex flex-wrap">
                  <div
                    v-for="(label, i) in barchartLabel"
                    :key="i"
                    class="legends d-flex align-items-center mb-2"
                  >
                    <span
                      :style="'background-color:' + barchartBGColor[i]"
                      class="legend--bgcolor mr-2"
                    ></span>
                    <span>{{ label }}</span>
                  </div>
                </div>
                <BarChart
                  :chart-data="conferenceThisMonthCollection"
                  :options="conferenceChartOptions"
                />
              </div>
              <Spinner :theme="primary" v-else />
              <div class="barchart--holder" v-if="barchartLabel.length != 0">
                <h6>Last Month</h6>
                <div class="d-flex flex-wrap">
                  <div
                    v-for="(label, i) in barchartLabel"
                    :key="i"
                    class="legends d-flex align-items-center mb-2"
                  >
                    <span
                      :style="'background-color:' + barchartBGColor[i]"
                      class="legend--bgcolor mr-2"
                    ></span>
                    <span>{{ label }}</span>
                  </div>
                </div>
                <BarChart
                  :chart-data="conferenceLastMonthCollection"
                  :options="conferenceChartOptions"
                />
              </div>
              <Spinner :theme="primary" v-else />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Spinner from "@/components/Spinner";
import LineChart from "@/modules/admin/elements/charts/Line";
import BarChart from "@/modules/admin/elements/charts/Bar";
import moment from "@/helpers/moment";
import axios from "@/helpers/axios";

export default {
  name: "overview",
  components: {
    LineChart,
    Spinner,
    BarChart
  },
  computed: {
    orders() {
      return this.$store.state.concierge.orders;
    },
    events() {
      return this.$store.state.concierge.eventsList.data;
    },
    primary() {
      return this.$store.state.app.scheme.primary;
    },
    secondary() {
      return this.$store.state.app.scheme.secondary;
    },
    event() {
      return this.$store.state.app.scheme.event;
    },
    danger() {
      return this.$store.state.app.scheme.danger;
    }
  },
  async created() {
    await this.getBookings();
    await this.getConferences();
    await this.getRecentEvents();
  },
  async mounted() {
    /**
     * Realtime event socket channel
     */
    let eventChannels = ["notifyNewEvent", "notifyRemoveEvent"];

    eventChannels.forEach(async event => {
      await this.$socket.on(event, async data => {
        const tenant = window.location.host.split(".")[0];
        if (data.tenant == tenant) {
          await this.getRecentEvents();
        }
      });
    });

    await this.$socket.on("notifyPaidOrder", async data => {
      const tenant = window.location.host.split(".")[0];
      if (data.tenant == tenant) {
        await this.getBookings();
        await this.getConferences();
      }
    });
  },
  data() {
    return {
      currentDate: moment().format("LL"),
      lastMonth: moment()
        .subtract(1, "month")
        .format("LL"),
      coworkingCount: 0,
      conferenceCount: 0,
      assignedLeadsCount: 10,
      followupLeadsCount: 25,
      closedLeadsCount: 11,
      projectedSales: [
        {
          month: moment().format("MMMM"),
          amount: 0
        },
        {
          month: moment()
            .subtract(1, "month")
            .format("MMMM"),
          amount: 0
        }
      ],
      isComputed: false,
      salesCollection: {},
      usageCollection: {},
      conferenceThisMonthCollection: {
        labels: [],
        datasets: [
          {
            backgroundColor: [],
            data: [],
            fill: false
          }
        ]
      },
      conferenceLastMonthCollection: {
        labels: [],
        datasets: [
          {
            backgroundColor: [],
            data: [],
            fill: false
          }
        ]
      },
      salesChartOptions: {},
      usageChartOptions: {},
      conferenceChartOptions: {},
      isExpanded: false,
      thisYear: moment().format("YYYY"),
      barchartLabel: [],
      barchartBGColor: []
    };
  },
  methods: {
    async getRecentEvents() {
      let eventParams = {
        number: 1,
        size: 5
      };
      await this.$store.dispatch(
        "fetchRecentEvents",
        `events?page[number]=${eventParams.number}&page[size]=${eventParams.size}`
      );
    },
    async getTotalSalesAndRecentEvents() {
      let month = moment()
        .subtract(1, "months")
        .format("M");
      let year = moment().format("YYYY");
      await this.$store.dispatch(
        "fetchTotalSales",
        `orders/getTotals?month=${month}&year=${year}`
      );
    },
    async getConferences() {
      this.barchartLabel = [];
      this.barchartBGColor = [];
      let conferences = await axios.get(`/products?categoryId=1&status=2`);
      let products = conferences.data.data;

      // Conferences this month
      let confyCount = await axios.get(
        `/orders/getOrdersByMonth?month=${this.currentDate}`
      );

      // Conferences last month
      let confyLastCount = await axios.get(
        `/orders/getOrdersByMonth?month=${this.lastMonth}`
      );

      Object.keys(products).map(el => {
        let productName = products[el].name;
        let dataCount = confyCount.data.groupConferences[productName]
          ? confyCount.data.groupConferences[productName].length
          : 0;

        let dataCountLastMonth = confyLastCount.data.groupConferences[
          productName
        ]
          ? confyLastCount.data.groupConferences[productName].length
          : 0;
        let randomColors =
          "#" +
          (
            "000000" +
            Math.random()
              .toString(16)
              .slice(2, 8)
              .toUpperCase()
          ).slice(-6);
        // This Month
        this.conferenceThisMonthCollection.labels.push(products[el].name);
        this.conferenceThisMonthCollection.datasets[0].backgroundColor.push(
          randomColors
        );
        this.conferenceThisMonthCollection.datasets[0].data.push(dataCount);

        // Last Month
        this.conferenceLastMonthCollection.labels.push(products[el].name);
        this.conferenceLastMonthCollection.datasets[0].backgroundColor.push(
          randomColors
        );
        this.conferenceLastMonthCollection.datasets[0].data.push(
          dataCountLastMonth
        );
        this.barchartLabel.push(products[el].name);
        this.barchartBGColor.push(randomColors);
      });
    },
    async readyChartVariables() {
      (this.salesChartOptions = {
        responsive: true,
        title: {
          display: true,
          fontColor: "#232323",
          fontSize: 16,
          fontFamily: "Gotham",
          fontWeight: 500
        },
        tooltips: {
          mode: "index",
          intersect: false,
          callbacks: {
            label: tooltipItem =>
              `₱ ${this.formatNumber(
                Math.round(tooltipItem.yLabel * 100) / 100
              )}`
          }
        },
        hover: {
          mode: "nearest",
          intersect: true
        },
        scales: {
          yAxes: [
            {
              scaleLabel: {
                display: true,
                labelString: "Sales",
                fontColor: "#232323",
                fontFamily: "Gotham Book",
                fontSize: 12
              },
              ticks: {
                fontColor: "#232323",
                fontFamily: "Gotham Book",
                fontSize: 10
              }
            }
          ],
          xAxes: [
            {
              ticks: {
                fontColor: "#232323",
                fontFamily: "Gotham Book",
                fontSize: 10
              }
            }
          ]
        }
      }),
        (this.salesCollection = {
          labels: [
            "January",
            "February",
            "March",
            "April",
            "May",
            "June",
            "July",
            "August",
            "September",
            "October",
            "November",
            "December"
          ],
          datasets: [
            {
              label: "Conference Rooms",
              backgroundColor: "red",
              borderColor: "red",
              data: [],
              fill: false
            },
            {
              label: "Desks",
              backgroundColor: "gold",
              borderColor: "gold",
              data: [],
              fill: false
            }
          ]
        });
      this.usageCollection = {
        labels: [
          "January",
          "February",
          "March",
          "April",
          "May",
          "June",
          "July",
          "August",
          "September",
          "October",
          "November",
          "December"
        ],
        datasets: [
          {
            label: "Conference Rooms",
            backgroundColor: "cornflowerblue",
            borderColor: "cornflowerblue",
            data: [],
            fill: false
          },
          {
            label: "Desks",
            backgroundColor: "magenta",
            borderColor: "magenta",
            data: [],
            fill: false
          }
        ]
      };
      this.usageChartOptions = {
        responsive: true,
        title: {
          display: true,
          fontColor: "#232323",
          fontSize: 16,
          fontFamily: "Gotham",
          fontWeight: 500
        },
        tooltips: {
          mode: "index",
          intersect: false
        },
        hover: {
          mode: "nearest",
          intersect: true
        },
        scales: {
          yAxes: [
            {
              scaleLabel: {
                display: true,
                labelString: "Number of Bookings",
                fontColor: "#232323",
                fontFamily: "Gotham Book",
                fontSize: 12
              },
              ticks: {
                fontColor: "#232323",
                fontFamily: "Gotham Book",
                fontSize: 10
              }
            }
          ],
          xAxes: [
            {
              ticks: {
                fontColor: "#232323",
                fontFamily: "Gotham Book",
                fontSize: 10
              }
            }
          ]
        }
      };
      this.conferenceChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        title: {
          display: true,
          fontColor: "#232323",
          fontSize: 16,
          fontFamily: "Gotham",
          fontWeight: 500
        },
        tooltips: {
          mode: "index",
          intersect: false
        },
        hover: {
          mode: "nearest",
          intersect: true
        },
        scales: {
          yAxes: [
            {
              ticks: {
                fontColor: "#232323",
                fontFamily: "Gotham Book",
                fontSize: 10,
                suggestedMin: 0
              }
            }
          ],
          xAxes: [
            {
              display: false,
              ticks: {
                fontColor: "#232323",
                fontFamily: "Gotham Book",
                fontSize: 10
              }
            }
          ]
        },
        legend: {
          display: true,
          position: top
        }
      };
    },
    async getBookings() {
      await this.readyChartVariables();
      let categories = [1, 2];
      categories.forEach(async val => {
        let bookings = await axios.get(
          `/categories/${val}/orders?date=${this.currentDate}`
        );
        Object.keys(bookings.data.data).map(el => {
          if (val == 1) {
            // Conference
            this.conferenceCount += bookings.data.data[el].length;
          } else if (val == 2) {
            // Coworking
            this.coworkingCount += bookings.data.data[el].length;
          }
        });
      });
      let usage = await axios.get(
        `/orders/getOrdersByYear?year=${this.thisYear}`
      );
      let sales = await axios.get(
        `/orders/getOrdersByYear?year=${this.thisYear}&status=Paid`
      );
      let salesConference = sales.data.conference.months;
      let salesCoworking = sales.data.coworking.months;
      let currentMonth = parseInt(moment().format("M")) - 1;

      this.projectedSales[0].amount =
        salesConference[currentMonth].sale + salesCoworking[currentMonth].sale;
      this.projectedSales[1].amount =
        salesConference[currentMonth - 1].sale +
        salesCoworking[currentMonth - 1].sale;

      this.isComputed = true;

      /**
       * Sales Chart
       */
      salesConference.forEach(month => {
        this.salesCollection.datasets[0].data.push(month.sale);
      });
      salesCoworking.forEach(month => {
        this.salesCollection.datasets[1].data.push(month.sale);
      });

      /**
       * Usage Chart
       */
      let conferences = usage.data.conference.months;
      let coworking = usage.data.coworking.months;

      conferences.forEach(month => {
        this.usageCollection.datasets[0].data.push(month.count);
      });
      coworking.forEach(month => {
        this.usageCollection.datasets[1].data.push(month.count);
      });
    },
    handleShowMoreUsersClick() {
      this.isExpanded = true;
    },
    getRandomInt() {
      return Math.floor(Math.random() * (50 - 5 + 1)) + 5;
    },
    parseDate(date, format) {
      return moment(date).format(format);
    },
    formatNumber(num) {
      return num.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");
    }
  }
};
</script>

<style lang="scss" scoped>
.overview {
  padding: 0 15px;
  .content--main {
    height: calc(
      100vh - 183px
    ); /* height of viewport minus height of nav.header */
    overflow-y: auto;
    overflow-x: hidden;
    padding-bottom: 20px;
    padding-top: 20px;
    .block {
      background-color: $gray-50;
      border-radius: 5px;
      box-shadow: none;
      .block-header {
        border-bottom: 1px solid $gray-250;
      }
      padding: 20px 30px;
      h2 {
        color: $gray-900;
        font-family: "Gotham";
        font-size: $h2-font-size;
        font-weight: 500;
      }
      p {
        font-family: "Gotham Book";
        font-size: $font-size-sm;
        margin-bottom: 5px;
        span {
          font-family: "Gotham";
          font-size: $font-size-xl;
        }
        strong {
          font-family: "Gotham";
          font-weight: 500;
        }
      }
      h4 {
        color: $gray-900;
        font-family: "Gotham";
        font-size: $font-size-base;
        font-weight: 500;
        &.total-sales {
          margin-bottom: 25px;
          margin-top: 25px;
        }
      }
      table {
        background-color: $white;
        font-family: "Gotham";
        font-size: $font-size-xs;
        margin-top: 30px;
        thead {
          tr {
            td {
              font-weight: 500;
            }
          }
        }
        tbody {
          tr {
            td {
              font-family: "Gotham Book";
              &:first-child {
                font-family: "Gotham";
                font-weight: 500;
              }
            }
          }
        }
      }
      .badge-wrapper {
        .show-more-users {
          align-items: center;
          background-color: $gray-350;
          border: 5px solid transparent;
          border-radius: 50%;
          cursor: pointer;
          display: flex;
          height: 60px;
          justify-content: center;
          padding: 0;
          position: relative;
          width: 60px;
          span {
            background-color: $white;
            border-radius: 50%;
            display: block;
            height: 10px;
            margin-right: 3px;
            width: 10px;
            &:last-child {
              margin-right: 0;
            }
          }
        }
      }
      .event-holder {
        flex: auto;
        span {
          font-family: "Gotham";
          font-size: $font-size-xs;
        }
        a {
          text-decoration: none !important;
        }
      }
      .events-wrapper {
        .event {
          margin-top: 30px;
          h5 {
            color: $gray-900;
            font-size: $font-size-xs;
            margin-bottom: 10px;
          }
          p {
            font-family: "Gotham Book";
            margin-bottom: 10px;
          }
        }
      }
      .block--sales-month {
        div {
          flex: auto;
          span {
            font-size: $font-size-xs;
          }
          p {
            font-size: $font-size-lg;
          }
        }
      }
    }
    .block--sales-product {
      h6 {
        font-size: $h6-font-size;
      }
      table {
        tbody {
          tr {
            td {
              font-weight: 400;
            }
          }
        }
      }
    }
    .barchart--block {
      flex: auto;
      .barchart--holder {
        width: 50% !important;
        .legends {
          padding-right: 10px;
          font-size: $font-size-xs;
          .legend--bgcolor {
            width: 15px;
            height: 15px;
          }
        }
      }
    }
    .block--total-sales {
      .activity-holder {
        flex: auto;
        span {
          font-family: "Gotham";
          font-size: $font-size-xs;
        }
        a {
          text-decoration: none !important;
        }
      }
    }
  }
}
</style>
